import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faMusic } from '@fortawesome/pro-light-svg-icons/faMusic';
import { faAlignLeft } from '@fortawesome/pro-light-svg-icons/faAlignLeft';
import type { ReleaseSetup } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { useOpenSongPage } from '../../../../smartLinks/catalog/song/page/useOpenSongPage';
import { useShowUnlockCards } from './useShowUnlockCards';
import { UnlockCard } from './UnlockCard';
import { unlockMessages } from './unlockMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cards and the preview line */
import styles from './UnlockCards.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface UnlockCardsProps {
  catalogItemUuid: string;
  /** The setup check of the release (`GET /releases/:uuid`); undefined while it loads. */
  setup: ReleaseSetup | undefined;
  /**
   * The song is not out, has no release day and no pre-save page (the rule of `presaveNeedsDate`
   * in `onboarding.setFirstRelease`). The page then says how to get the pre-save page.
   */
  needsReleaseDay?: boolean;
}

/** The audio is missing while the master item is `missing` or `failed`. A master that is read counts as done. */
function isAudioMissing(setup: ReleaseSetup): boolean {
  const master = setup.items.find((item) => item.key === 'master');
  return master !== undefined && (master.state === 'missing' || master.state === 'failed');
}

/** The lyrics are missing while the song has no lyrics text. The timing is the setup check's job. */
function isLyricsMissing(setup: ReleaseSetup): boolean {
  return setup.items.some((item) => item.key === 'lyrics' && item.detail === 'noLyrics');
}

/**
 * The two unlock cards on the first release (design: onboarding c5): "Add the audio → videos" and
 * "Add the lyrics → lyric cards". Each opens the Catalog song page on its section. They show only
 * for the first-release song while the audio or the lyrics are missing. A part that is in keeps
 * its card, grey with a check, until the other part is in too; then the row goes. The old "Free
 * plan preview" line is gone: the watermark banner of the Kit tab says it (design: trial-upsell a4).
 * A song with no release day gets one more line: the pre-save page needs the day.
 */
const UnlockCardsComponent: React.FC<UnlockCardsProps> = ({ catalogItemUuid, setup, needsReleaseDay = false }) => {
  const intl = useIntl();
  const isFirstRelease = useShowUnlockCards(catalogItemUuid);
  const openSongPage = useOpenSongPage();
  const openFiles = useCallback(() => openSongPage(catalogItemUuid, 'files'), [catalogItemUuid, openSongPage]);
  const openLyrics = useCallback(() => openSongPage(catalogItemUuid, 'lyrics'), [catalogItemUuid, openSongPage]);
  const openDetails = useCallback(() => openSongPage(catalogItemUuid, 'details'), [catalogItemUuid, openSongPage]);

  if (!isFirstRelease || setup === undefined) {
    return null;
  }
  const needsAudio = isAudioMissing(setup);
  const needsLyrics = isLyricsMissing(setup);
  const showCards = needsAudio || needsLyrics;
  if (!showCards && !needsReleaseDay) {
    return null;
  }
  const doneLabel = intl.formatMessage(unlockMessages.done);

  return (
    <section className={styles.wrap} data-testid="unlock-cards">
      {showCards && (
        <div className={styles.cards}>
          <UnlockCard
            icon={faMusic}
            title={intl.formatMessage(unlockMessages.audioTitle)}
            body={intl.formatMessage(unlockMessages.audioBody)}
            action={intl.formatMessage(unlockMessages.audioAction)}
            isPrimary={needsAudio}
            isDone={!needsAudio}
            doneLabel={doneLabel}
            onAction={openFiles}
          />
          <UnlockCard
            icon={faAlignLeft}
            title={intl.formatMessage(unlockMessages.lyricsTitle)}
            body={intl.formatMessage(unlockMessages.lyricsBody)}
            action={intl.formatMessage(unlockMessages.lyricsAction)}
            isPrimary={needsLyrics && !needsAudio}
            isDone={!needsLyrics}
            doneLabel={doneLabel}
            onAction={openLyrics}
          />
        </div>
      )}
      {needsReleaseDay && (
        <div className={styles.preview} data-testid="unlock-release-day">
          <span className={styles.hintTag}>{intl.formatMessage(unlockMessages.releaseDayTag)}</span>
          <span className={styles.previewBody}>{intl.formatMessage(unlockMessages.releaseDayBody)}</span>
          <button type="button" className={styles.previewAction} onClick={openDetails}>
            {intl.formatMessage(unlockMessages.releaseDayAction)}
          </button>
        </div>
      )}
    </section>
  );
};

export const UnlockCards = TypedMemo(UnlockCardsComponent);
